import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ibanGueltig } from '@symphenia/shared-types'
import { Button } from '@/components/ui/button'
import { formCard, pageTitle, sectionTitle, fieldLabel } from '@/lib/form-styles'
import { setPdfDelivery } from '@/lib/export-delivery'
import { api } from '@/lib/axios'
import {
  brandingApi,
  LETTERHEAD_SLOT_FIELDS,
  type LetterheadSlot,
} from '../api/branding.api'
import { bestaetige } from '@/lib/dialoge'
import {
  FeldStatus,
  FeldStatusZeichen,
  feldKlasse,
  mitStand,
  useAenderung,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'

// ─── Konstanten ────────────────────────────────────────────────────────────────

const FONT_OPTIONS = ['Arial', 'Calibri', 'Times New Roman', 'Georgia', 'Verdana']

const DEFAULT_FONT_SIZES = { h1: 14, h2: 10, tableHeader: 9, tableBody: 8, tableSum: 8, footer: 7 }

/**
 * AP-06-B Regel 6 — klassische Briefbogen-Logik: je Ausrichtung ein Blatt für
 * die erste Seite und eines für die Folgeseiten. Fehlt eines, wird nicht
 * abgebrochen, sondern eine Stufe weitergerückt — die Hinweise sagen, wohin.
 */
const LETTERHEAD_FIELDS: { slot: LetterheadSlot; labelKey: string; hintKey: string }[] = [
  { slot: 'portrait',         labelKey: 'portraitFirst',   hintKey: 'portraitFirstHint' },
  { slot: 'portrait-follow',  labelKey: 'portraitFollow',  hintKey: 'portraitFollowHint' },
  { slot: 'landscape',        labelKey: 'landscapeFirst',  hintKey: 'landscapeFirstHint' },
  { slot: 'landscape-follow', labelKey: 'landscapeFollow', hintKey: 'landscapeFollowHint' },
]

const ORIENTATION_OPTIONS = [
  { val: 'auto',      labelKey: 'auto',      hintKey: 'autoHint' },
  { val: 'portrait',  labelKey: 'portrait',  hintKey: 'portraitHint' },
  { val: 'landscape', labelKey: 'landscape', hintKey: 'landscapeHint' },
] as const

const PDF_DELIVERY_OPTIONS = [
  { val: 'download', labelKey: 'download', hintKey: 'downloadHint' },
  { val: 'tab',      labelKey: 'tab',      hintKey: 'tabHint' },
] as const

const FILENAME_PLACEHOLDERS = [
  { key: '{modul}',        labelKey: 'modul' },
  { key: '{modul_label}',  labelKey: 'modulLabel' },
  { key: '{datum}',        labelKey: 'datum' },
  { key: '{datum_lang}',   labelKey: 'datumLang' },
  { key: '{zeitraum}',     labelKey: 'zeitraum' },
  { key: '{zeitraum_von}', labelKey: 'zeitraumVon' },
  { key: '{zeitraum_bis}', labelKey: 'zeitraumBis' },
  { key: '{mandant}',      labelKey: 'mandant' },
  { key: '{format}',       labelKey: 'format' },
]

// ─── Hilfskomponenten ──────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className={sectionTitle + ' mb-4 pb-2 border-b border-border'}>{children}</h2>
}

function FieldLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return <label htmlFor={htmlFor} className={fieldLabel}>{children}</label>
}

// ─── Drag&Drop Datei-Uploadzone ────────────────────────────────────────────────

function FileDropZone({
  accept,
  uploading,
  existingName,
  label,
  icon,
  onFile,
  onDelete,
}: {
  accept: string
  uploading: boolean
  existingName?: string | null
  label: string
  icon: React.ReactNode
  onFile: (f: File) => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) onFile(file)
  }, [onFile])

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = () => setDragOver(false)

  if (existingName) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <span className="flex-1 truncate text-foreground">📄 {existingName.split('/').pop()}</span>
        <button onClick={onDelete} className="text-destructive hover:underline text-xs shrink-0">{t('branding.letterhead.delete')}</button>
      </div>
    )
  }

  return (
    <>
      <div
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-6 transition-colors
          ${dragOver ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <span className="text-sm">{t('branding.letterhead.uploading')}</span>
        ) : (
          <>
            {icon}
            <span className="text-sm">{label}</span>
            <span className="text-xs opacity-60">{t('branding.letterhead.dropHint')}</span>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }} />
    </>
  )
}

// ─── Speicherstelle ────────────────────────────────────────────────────────────

const DEFAULTS: Datensatz = {
  letterheadPdfPath: null, letterheadFollowPath: null,
  letterheadLandscapePdfPath: null, letterheadLandscapeFollowPath: null,
  logoPath: null,
  pageOrientationMode: 'auto', autoLandscapeFromColumns: 6,
  exportPdfDelivery: 'download',
  marginTopMm: 15, marginBottomMm: 15, marginLeftMm: 15, marginRightMm: 15,
  logoAlign: 'right', logoOffsetXmm: 0, logoOffsetYmm: 0, logoWidthMm: 40,
  companyName: null, companyStreet: null, companyCity: null, companyCountry: 'Österreich',
  companyPhone: null, companyEmail: null, companyWeb: null, companyUid: null,
  companyIban: null, companyBic: null, companyBankName: null,
  fontFamily: 'Arial',
  fontSizes: { ...DEFAULT_FONT_SIZES },
  accentColors: ['#1e40af'],
  zebraEnabled: true, zebraColor: '#f1f5f9',
  filenameTemplate: '{modul}_{zeitraum}_{datum}',
}

/**
 * Werte der Stelle `branding`: die Vorgaben, darüber der gespeicherte Datensatz.
 * Solange es keine Zeile gibt, ist kein Stand da — das erste Speichern legt sie an.
 * `fontSizes` wird mit den Vorgaben aufgefüllt, damit jede Größe ein Feld hat.
 */
const brandingWerte = (a: unknown): Datensatz => {
  const b = (a && typeof a === 'object' ? a : {}) as Datensatz
  const gesetzt = Object.fromEntries(Object.entries(b).filter(([, v]) => v !== undefined))
  return { ...DEFAULTS, ...gesetzt, fontSizes: { ...DEFAULT_FONT_SIZES, ...((b.fontSizes as Datensatz | null) ?? {}) } }
}

// ─── Hauptkomponente ───────────────────────────────────────────────────────────

/**
 * AP-36 Phase D: ohne Knopf. Jedes Feld speichert beim Verlassen bzw. sofort
 * (Auswahl, Schalter); `fontSizes.*` geht als Pfad, der Server führt einen
 * Schlüssel tief zusammen. Die Schmuckfarben sind eine Liste und werden als
 * Operation auf dem aktuellen Stand geschrieben (8.3 a). Hochladen und Löschen
 * der Dateien sind Handlungen mit eigenem Endpunkt; danach liest die Seite den
 * Datensatz neu, damit der Stand stimmt.
 */
export default function BrandingPage() {
  const { t } = useTranslation()
  const [geladen, setGeladen] = useState<Datensatz | null>(null)
  const [uploading, setUploading] = useState<'logo' | LetterheadSlot | null>(null)
  const [error, setError] = useState<string | null>(null)

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'branding',
    bezeichnung: t('branding.title'),
    daten: geladen,
    werte: brandingWerte,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>('/branding', patch, mitStand(stand)).then((r) => {
        // Ohne das gölte für den Export bis zum nächsten Neuladen der alte Wert.
        if (patch.exportPdfDelivery !== undefined) setPdfDelivery(r.data.exportPdfDelivery === 'tab' ? 'tab' : 'download')
        return r.data
      }),
    keepalive: { url: '/branding', method: 'PATCH' },
  })
  const s = stelle as Speicherstelle<unknown>
  const branding = useStellenDaten(s)

  const neuLaden = () =>
    brandingApi.get().then((r) => {
      if (r.data) stelle.uebernimm(r.data as unknown as Datensatz)
    })

  useEffect(() => {
    brandingApi.get()
      // Ohne gespeicherte Zeile ist der Körper leer ('') — das zählt als geladen, mit den Vorgaben.
      .then((r) => setGeladen((r.data || {}) as unknown as Datensatz))
      .catch(() => setGeladen({}))
  }, [])

  if (!geladen) {
    return <div className="p-6 text-muted-foreground">{t('branding.loading')}</div>
  }

  // ── Datei-Uploads ──────────────────────────────────────────────────────────
  // Der Server prüft Typ und Größe (AP-11-E) und begründet eine Ablehnung
  // konkret — etwa „PDF-Dateien sind hier nicht zulässig". Diese Meldung ist
  // für den Benutzer weit brauchbarer als ein pauschales „fehlgeschlagen".
  const uploadErrorText = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback

  const handleLogoUpload = async (file: File) => {
    setUploading('logo'); setError(null)
    try {
      await brandingApi.uploadLogo(file)
      await neuLaden()
    } catch (err) { setError(uploadErrorText(err, t('branding.letterhead.uploadFailedLogo'))) }
    finally { setUploading(null) }
  }

  // Ein Weg für alle vier Plätze: Die Antwort des Servers nennt das Feld, das
  // er gesetzt hat — die Maske muss die Zuordnung nicht doppelt kennen.
  const handleLetterheadUpload = async (slot: LetterheadSlot, file: File) => {
    setUploading(slot); setError(null)
    try {
      await brandingApi.uploadLetterhead(slot, file)
      await neuLaden()
    } catch (err) { setError(uploadErrorText(err, t('branding.letterhead.uploadFailedLetterhead'))) }
    finally { setUploading(null) }
  }

  const handleDeleteLogo = async () => {
    if (!(await bestaetige(t('branding.letterhead.confirmDeleteLogo')))) return
    await brandingApi.deleteLogo()
    await neuLaden()
  }

  const handleDeleteLetterhead = async (slot: LetterheadSlot) => {
    if (!(await bestaetige(t('branding.letterhead.confirmDeleteLetterhead')))) return
    await brandingApi.deleteLetterhead(slot)
    await neuLaden()
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8 p-4 sm:p-6">

      {/* Seitentitel */}
      <div>
        <h1 className={pageTitle}>{t('branding.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('branding.subtitle')}</p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {/* ── 1. Briefpapier & Logo ─────────────────────────────────────────── */}
      <div className={formCard}>
        <SectionTitle>{t('branding.letterhead.section')}</SectionTitle>
        <p className="mb-4 text-xs text-muted-foreground">
          {t('branding.letterhead.intro')}
          <strong className="block mt-1">{t('branding.letterhead.format')}</strong>
        </p>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {LETTERHEAD_FIELDS.map(({ slot, labelKey, hintKey }) => (
            <div key={slot} className="space-y-2">
              <FieldLabel>{t(`branding.letterhead.${labelKey}`)}</FieldLabel>
              <FileDropZone
                accept="image/png"
                uploading={uploading === slot}
                existingName={branding[LETTERHEAD_SLOT_FIELDS[slot]] as string | null | undefined}
                label={t('branding.letterhead.upload')}
                icon={<svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" /></svg>}
                onFile={(f) => handleLetterheadUpload(slot, f)}
                onDelete={() => handleDeleteLetterhead(slot)}
              />
              <p className="text-xs text-muted-foreground">{t(`branding.letterhead.${hintKey}`)}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {/* Logo */}
          <div className="space-y-2">
            <FieldLabel>{t('branding.letterhead.logo')}</FieldLabel>
            <FileDropZone
              accept="image/png,image/svg+xml,image/jpeg"
              uploading={uploading === 'logo'}
              existingName={branding.logoPath as string | null}
              label={t('branding.letterhead.uploadImage')}
              icon={<svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>}
              onFile={handleLogoUpload}
              onDelete={handleDeleteLogo}
            />
          </div>
        </div>

        <Ausrichtung stelle={s} />
        <PdfAusgabe stelle={s} />

        {/* PDF-Ränder */}
        <div className="mt-6 border-t border-border pt-5">
          <p className={fieldLabel + ' mb-1'}>{t('branding.margins.title')}</p>
          <p className="mb-3 text-xs text-muted-foreground">{t('branding.margins.hint')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {([
              { key: 'marginTopMm',    labelKey: 'top' },
              { key: 'marginBottomMm', labelKey: 'bottom' },
              { key: 'marginLeftMm',   labelKey: 'left' },
              { key: 'marginRightMm',  labelKey: 'right' },
            ] as const).map(({ key, labelKey }) => (
              <ZahlFeld key={key} stelle={s} feld={key} label={t(`branding.margins.${labelKey}`)} min={0} max={100} einheit="mm" />
            ))}
          </div>
        </div>

        <LogoPosition stelle={s} />
      </div>

      {/* ── 2. Absender-Struktur ──────────────────────────────────────────── */}
      <div className={formCard}>
        <SectionTitle>{t('branding.sender.section')}</SectionTitle>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { key: 'companyName',    labelKey: 'companyName', placeholder: 'Muster GmbH' },
            { key: 'companyStreet',  labelKey: 'street',      placeholder: 'Musterstraße 1' },
            { key: 'companyCity',    labelKey: 'city',        placeholder: '1010 Wien' },
            { key: 'companyCountry', labelKey: 'country',     placeholder: 'Österreich' },
            { key: 'companyPhone',   labelKey: 'phone',       placeholder: '+43 1 234 567' },
            { key: 'companyEmail',   labelKey: 'email',       placeholder: 'office@firma.at' },
            { key: 'companyWeb',     labelKey: 'web',         placeholder: 'www.firma.at' },
            { key: 'companyUid',     labelKey: 'uid',         placeholder: 'ATU12345678' },
            { key: 'companyIban',    labelKey: 'iban',        placeholder: 'AT61 1904 3002 3457 3201' },
            { key: 'companyBic',     labelKey: 'bic',         placeholder: 'BKAUATWW' },
            { key: 'companyBankName', labelKey: 'bank',       placeholder: 'Musterbank' },
          ].map(({ key, labelKey, placeholder }) => (
            <TextFeld
              key={key}
              stelle={s}
              feld={key}
              label={t(`branding.sender.${labelKey}`)}
              placeholder={placeholder}
              // Weich wie am Geschäftspartner: gespeichert wird auch eine falsche IBAN, gesagt wird es hier — sonst merkt es
              // erst der Zahlungslauf der Eingangsrechnungen (AP-34 Nachtrag 6, PT-24).
              warnung={key === 'companyIban' ? (wert) => (wert.trim() && !ibanGueltig(wert) ? t('branding.sender.ibanInvalid') : null) : undefined}
            />
          ))}
        </div>
      </div>

      {/* ── 3. Typografie ─────────────────────────────────────────────────── */}
      <Typografie stelle={s} />

      {/* ── 4. Farben ─────────────────────────────────────────────────────── */}
      <Farben stelle={s} />

      {/* ── 5. Dateinamenskonvention ──────────────────────────────────────── */}
      <Dateiname stelle={s} />
    </div>
  )
}

// ─── Felder ────────────────────────────────────────────────────────────────────

const kleinesFeld = `${feldKlasse} disabled:opacity-40`

function TextFeld({ stelle, feld, label, placeholder, pflicht, warnung }: {
  stelle: Speicherstelle<unknown>; feld: string; label: string; placeholder?: string; pflicht?: boolean
  /** Hinweis zu dem, was im Feld steht — sperrt das Speichern nicht. */
  warnung?: (wert: string) => string | null
}) {
  const f = useFeld(stelle, feld, { label, pflicht })
  const hinweis = warnung?.(String(f.props.value ?? '')) ?? null
  return (
    <div className="space-y-1">
      <FieldLabel htmlFor={feld}>{label}</FieldLabel>
      <input id={feld} type="text" placeholder={placeholder} className={feldKlasse} {...f.props} />
      <FeldStatus status={f} />
      {hinweis && <p role="status" className="text-xs text-amber-700 dark:text-amber-300">{hinweis}</p>}
    </div>
  )
}

function ZahlFeld({ stelle, feld, label, min, max, einheit, disabled }: {
  stelle: Speicherstelle<unknown>; feld: string; label: string; min: number; max: number; einheit?: string; disabled?: boolean
}) {
  const f = useFeld<number | null>(stelle, feld, {
    art: 'zahl',
    label,
    pflicht: true,
    pruefe: (w) => (w !== null && (!Number.isInteger(w) || w < min || w > max) ? `${min} bis ${max}` : null),
  })
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <div className="flex items-center gap-1">
        <input inputMode="numeric" className={kleinesFeld} {...f.props} disabled={disabled} />
        {einheit && <span className="text-xs text-muted-foreground shrink-0">{einheit}</span>}
        <FeldStatusZeichen status={f} />
      </div>
      <FeldStatus status={f} zeichen={false} />
    </div>
  )
}

/** Knöpfe als Auswahl — speichert sofort. */
function WahlKnoepfe<T extends string>({ stelle, feld, label, optionen, breit }: {
  stelle: Speicherstelle<unknown>
  feld: string
  label: string
  optionen: readonly { val: T; text: string; hinweis?: string }[]
  breit?: boolean
}) {
  const f = useFeld<T>(stelle, feld, { art: 'auswahl', label, lies: (x) => x as T })
  return (
    <div>
      <div className={breit ? 'space-y-2' : 'flex flex-wrap items-center gap-2'}>
        {optionen.map(({ val, text, hinweis }) => {
          const aktiv = f.wert === val
          return breit ? (
            <button
              key={val}
              type="button"
              onClick={() => f.setze(val)}
              aria-pressed={aktiv}
              className={`w-full rounded-md border px-4 py-3 text-left transition-colors ${
                aktiv ? 'border-primary bg-primary/10' : 'border-input hover:border-primary/40'
              }`}
            >
              <span className={`block text-sm font-medium ${aktiv ? 'text-primary' : 'text-foreground'}`}>{text}</span>
              {hinweis && <span className="mt-0.5 block text-xs text-muted-foreground">{hinweis}</span>}
            </button>
          ) : (
            <button
              key={val}
              type="button"
              onClick={() => f.setze(val)}
              title={hinweis}
              aria-pressed={aktiv}
              className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                aktiv ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input text-muted-foreground hover:text-foreground'
              }`}
            >
              {text}
            </button>
          )
        })}
        {!breit && <FeldStatusZeichen status={f} />}
      </div>
      <FeldStatus status={f} zeichen={breit} />
    </div>
  )
}

/** Standard-Ausrichtung (AP-06-B Regel 3) */
function Ausrichtung({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const modus = useStellenDaten(stelle).pageOrientationMode ?? 'auto'
  const spalten = useFeld<number | null>(stelle, 'autoLandscapeFromColumns', {
    art: 'zahl',
    label: t('branding.orientation.title'),
    pflicht: true,
    pruefe: (w) => (w !== null && (!Number.isInteger(w) || w < 1 || w > 50) ? '1 bis 50' : null),
  })
  return (
    <div className="mt-6 border-t border-border pt-5">
      <p className={fieldLabel + ' mb-1'}>{t('branding.orientation.title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">{t('branding.orientation.hint')}</p>
      <WahlKnoepfe
        stelle={stelle}
        feld="pageOrientationMode"
        label={t('branding.orientation.title')}
        optionen={ORIENTATION_OPTIONS.map(({ val, labelKey, hintKey }) => ({
          val,
          text: t(`branding.orientation.${labelKey}`),
          hinweis: t(`branding.orientation.${hintKey}`),
        }))}
      />
      {modus === 'auto' && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">{t('branding.orientation.thresholdBefore')}</label>
            <input inputMode="numeric" className={`${feldKlasse} w-20`} {...spalten.props} />
            <span className="text-xs text-muted-foreground">{t('branding.orientation.thresholdAfter')}</span>
            <FeldStatusZeichen status={spalten} />
          </div>
          <FeldStatus status={spalten} zeichen={false} />
        </div>
      )}
    </div>
  )
}

/** PDF-Ausgabe: Download oder Browser-Tab */
function PdfAusgabe({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  return (
    <div className="mt-6 border-t border-border pt-5">
      <p className={fieldLabel + ' mb-1'}>{t('branding.pdfDelivery.title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">{t('branding.pdfDelivery.hint')}</p>
      <WahlKnoepfe
        stelle={stelle}
        feld="exportPdfDelivery"
        label={t('branding.pdfDelivery.title')}
        breit
        optionen={PDF_DELIVERY_OPTIONS.map(({ val, labelKey, hintKey }) => ({
          val,
          text: t(`branding.pdfDelivery.${labelKey}`),
          hinweis: t(`branding.pdfDelivery.${hintKey}`),
        }))}
      />
    </div>
  )
}

function LogoPosition({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const mitte = (useStellenDaten(stelle).logoAlign ?? 'right') === 'center'
  return (
    <div className="mt-6 border-t border-border pt-5">
      <p className={fieldLabel + ' mb-1'}>{t('branding.logoPosition.title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">{t('branding.logoPosition.hint')}</p>
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t('branding.logoPosition.alignment')}</label>
          <WahlKnoepfe
            stelle={stelle}
            feld="logoAlign"
            label={t('branding.logoPosition.alignment')}
            optionen={(['left', 'center', 'right'] as const).map((val) => ({ val, text: t(`branding.logoPosition.${val}`) }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <ZahlFeld stelle={stelle} feld="logoOffsetXmm" label={t('branding.logoPosition.offsetX')} min={0} max={150} einheit="mm" disabled={mitte} />
          <ZahlFeld stelle={stelle} feld="logoOffsetYmm" label={t('branding.logoPosition.offsetY')} min={0} max={150} einheit="mm" />
          <ZahlFeld stelle={stelle} feld="logoWidthMm" label={t('branding.logoPosition.width')} min={10} max={120} einheit="mm" />
        </div>
      </div>
    </div>
  )
}

function Typografie({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const schrift = useFeld(stelle, 'fontFamily', { art: 'auswahl', label: t('branding.typography.font'), pflicht: true })
  const labelMap: Record<string, string> = {
    h1: t('branding.typography.h1'), h2: t('branding.typography.h2'),
    tableHeader: t('branding.typography.tableHeader'),
    tableBody: t('branding.typography.tableBody'),
    tableSum: t('branding.typography.tableSum'),
    footer: t('branding.typography.footer'),
  }
  return (
    <div className={formCard}>
      <SectionTitle>{t('branding.typography.section')}</SectionTitle>
      <div className="space-y-4">
        <div className="space-y-1">
          <FieldLabel htmlFor="fontFamily">{t('branding.typography.font')}</FieldLabel>
          <select id="fontFamily" className={feldKlasse} {...schrift.props}>
            {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <FeldStatus status={schrift} />
        </div>

        <div>
          <p className={fieldLabel + ' mb-2'}>{t('branding.typography.sizes')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Object.keys(DEFAULT_FONT_SIZES).map((key) => (
              <ZahlFeld key={key} stelle={stelle} feld={`fontSizes.${key}`} label={labelMap[key] ?? key} min={6} max={24} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Farbwähler, der erst beim Schließen übernimmt (der native `change`, nicht jede Bewegung). */
function FarbWaehler({ wert, onWahl, title }: { wert: string; onWahl: (hex: string) => void; title: string }) {
  const ref = useRef<HTMLInputElement>(null)
  const [vorschau, setVorschau] = useState(wert)
  useEffect(() => setVorschau(wert), [wert])
  const wahl = useRef(onWahl)
  wahl.current = onWahl
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const beiChange = () => { if (el.value !== wert) wahl.current(el.value) }
    el.addEventListener('change', beiChange)
    return () => el.removeEventListener('change', beiChange)
  }, [wert])
  return (
    <>
      <input
        ref={ref}
        type="color"
        value={vorschau}
        onChange={(e) => setVorschau(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded border border-input"
        title={title}
      />
      <span className="text-xs text-muted-foreground font-mono">{vorschau}</span>
    </>
  )
}

function Farben({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const daten = useStellenDaten(stelle)
  const farben = (daten.accentColors as string[] | undefined) ?? ['#1e40af']
  const { sende, status } = useAenderung(stelle, 'accentColors')
  const zebra = useFeld<boolean>(stelle, 'zebraEnabled', { art: 'schalter', label: t('branding.colors.zebra') })
  const zebraFarbe = useFeld(stelle, 'zebraColor', { art: 'auswahl', label: t('branding.colors.zebraColor'), pflicht: true })

  /** Die Liste als Operation auf dem aktuellen Stand (8.3 a). */
  const aendere = (neu: (liste: string[]) => string[]) =>
    void sende({
      felder: ['accentColors'],
      labels: { accentColors: t('branding.colors.accent') },
      operation: (aktuell) => ({ accentColors: neu([...((aktuell.accentColors as string[] | undefined) ?? ['#1e40af'])]).slice(0, 3) }),
    })

  return (
    <div className={formCard}>
      <SectionTitle>{t('branding.colors.section')}</SectionTitle>

      <div className="space-y-4">
        <div>
          <p className={fieldLabel + ' mb-2'}>{t('branding.colors.accent')}</p>
          <div className="flex flex-wrap items-center gap-3">
            {farben.map((color, i) => (
              <div key={i} className="flex items-center gap-2">
                <FarbWaehler
                  wert={color}
                  title={`Schmuckfarbe ${i + 1}`}
                  onWahl={(hex) => aendere((l) => l.map((c, j) => (j === i ? hex : c)))}
                />
                {farben.length > 1 && (
                  <button type="button" onClick={() => aendere((l) => l.filter((_, j) => j !== i))} className="text-xs text-destructive hover:underline">✕</button>
                )}
              </div>
            ))}
            {farben.length < 3 && (
              <Button variant="outline" size="sm" onClick={() => aendere((l) => [...l, '#000000'])}>{t('branding.colors.addColor')}</Button>
            )}
            <FeldStatusZeichen status={status} />
          </div>
          <FeldStatus status={status} zeichen={false} />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...zebra.schalter} className="h-4 w-4 rounded border-input" />
            <span className={fieldLabel + ' mb-0'}>{t('branding.colors.zebra')}</span>
            <FeldStatusZeichen status={zebra} />
          </label>
          {zebra.wert && (
            <div className="flex items-center gap-2">
              <FarbWaehler wert={(zebraFarbe.wert as string | null) ?? '#f1f5f9'} title={t('branding.colors.zebraColor')} onWahl={(hex) => zebraFarbe.setze(hex)} />
              <FeldStatusZeichen status={zebraFarbe} />
            </div>
          )}
        </div>
        <FeldStatus status={zebra} zeichen={false} />
        <FeldStatus status={zebraFarbe} zeichen={false} />
      </div>
    </div>
  )
}

function Dateiname({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const vorlage = useFeld(stelle, 'filenameTemplate', { label: t('branding.filename.template'), pflicht: true })
  const text = (vorlage.props.value as string) ?? ''
  return (
    <div className={formCard}>
      <SectionTitle>{t('branding.filename.section')}</SectionTitle>
      <div className="space-y-3">
        <div className="space-y-1">
          <FieldLabel htmlFor="filenameTemplate">{t('branding.filename.template')}</FieldLabel>
          <input id="filenameTemplate" type="text" placeholder="{modul}_{zeitraum}_{datum}" className={feldKlasse} {...vorlage.props} />
          <FeldStatus status={vorlage} />
          <p className="text-xs text-muted-foreground">
            {t('branding.filename.preview')} <code className="rounded bg-muted px-1 py-0.5 text-xs">
              {text
                .replace('{modul}', 'zeiterfassung')
                .replace('{modul_label}', 'Zeiterfassung')
                .replace('{datum}', '20260410')
                .replace('{datum_lang}', '10_April_2026')
                .replace('{zeitraum}', '2026-03')
                .replace('{zeitraum_von}', '2026-03-01')
                .replace('{zeitraum_bis}', '2026-03-31')
                .replace('{mandant}', 'abc12345')
                .replace('{format}', 'xlsx')
              }.xlsx
            </code>
          </p>
        </div>

        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
            {t('branding.filename.showPlaceholders')}
          </summary>
          <div className="mt-2 space-y-1">
            {FILENAME_PLACEHOLDERS.map(({ key, labelKey }) => (
              <div key={key} className="flex items-start gap-2 text-xs">
                <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-foreground">{key}</code>
                <span className="text-muted-foreground">{t(`branding.filename.placeholders.${labelKey}`)}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  )
}
